import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const EASE = [0.76, 0, 0.24, 1];

const chapters = [
  {
    n: "01",
    title: "Strategy first, always.",
    body:
      "Every campaign begins with a sharp point of view. We build brand narratives that intersect data with desire — where audience insight, cultural context, and clear commercial intent shape every creative decision.",
  },
  {
    n: "02",
    title: "Craft the frame, then fill it.",
    body:
      "Aesthetic is not decoration — it is the message. From editorial shoots to campaign design, we obsess over lighting, cadence, and composition so that beauty is legible even before words are read.",
  },
  {
    n: "03",
    title: "Performance is proof.",
    body:
      "Beautiful without results is a mood board. We hold ourselves to measurable growth — clicks, conversions, community, and share of attention — while never trading elegance for shortcuts.",
  },
];

export const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [-40, 120]);

  return (
    <section
      id="manifesto"
      ref={ref}
      className="relative border-t hairline py-24 md:py-40 overflow-hidden"
      data-testid="manifesto-section"
    >
      <div className="grid grid-cols-12 px-6 md:px-10 lg:px-14 mb-16">
        <div className="col-span-12 md:col-span-3 label">Manifesto</div>
        <div className="col-span-12 md:col-span-9 font-serif italic text-2xl md:text-3xl text-ink/80 mt-4 md:mt-0">
          Three principles, one philosophy.
        </div>
      </div>

      <div className="flex flex-col">
        {chapters.map((c, i) => (
          <motion.article
            key={c.n}
            initial={{ opacity: 0, y: 80 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1, ease: EASE, delay: i * 0.05 }}
            className="grid grid-cols-12 gap-6 md:gap-10 border-t hairline px-6 md:px-10 lg:px-14 py-14 md:py-20"
            data-testid={`manifesto-chapter-${c.n}`}
          >
            <div className="col-span-12 md:col-span-4">
              <span className="font-serif italic text-7xl md:text-9xl text-ink/70 leading-none">
                {c.n}
              </span>
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-5">
              <h3 className="font-serif text-3xl md:text-5xl leading-tight text-ink">
                {c.title}
              </h3>
              <p className="font-sans text-base md:text-lg text-ink/75 mt-6 max-w-xl leading-relaxed">
                {c.body}
              </p>
            </div>
          </motion.article>
        ))}
      </div>

      {/* Parallax accent image */}
      <motion.div
        style={{ y: imgY }}
        className="pointer-events-none absolute right-0 top-32 w-[26vw] max-w-[320px] aspect-[3/4] overflow-hidden hidden md:block opacity-70"
        aria-hidden="true"
      >
        <img
          src="https://images.unsplash.com/photo-1664076458686-3449062080ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxlZGl0b3JpYWwlMjBmYXNoaW9uJTIwcGhvdG9zaG9vdCUyMHN0dWRpb3xlbnwwfHx8fDE3ODQ3MTY0ODR8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="w-full h-full object-cover gray-hover"
        />
      </motion.div>
    </section>
  );
};

export default Manifesto;
